import React, {
  useRef,
  useEffect,
  useState,
  useMemo,
  useCallback,
  memo,
} from 'react';
import styled from 'styled-components';
import { Title, Text } from '../../../../components';
import { RedArrow } from '../../../../assets/svg';
import { COLORS, device, ROUTES } from '../../../../constants';
import { Link } from 'react-router-dom';
import { WhiteArrow } from '../../../../assets/svg';
import { PortfolioHome } from '../../../../assets/png';
import { Tag } from '../../../../components/Tag/Tag';
import { SHADOW } from '../../../../constants/animations';
import { useTranslation } from 'react-i18next';
import { MotionPortfolioProjectCard } from '../../../../components/Motion';

type PortfolioCardProps = {
  title: string;
  projectId: string | number;
  text?: string;
  titleColor?: string;
  category?: string[];
  expertises: string[];
  platforms?: string[];
  domain?: string;
  technologies?: string[];
  imageUrl?: string;
  width?: string;
  flexDirection?: string;
  arrowBorder?: React.CSSProperties['border'];
  arrowBackground?: string;
  textBackground?: string;
  descriptionColor?: string;
  titleSize?: number;
  titleWeight?: number;
  titleLineHeight?: number;
  tagBorder?: React.CSSProperties['border'];
  arrowSpace?: number;
  alignContent?: string;
  caption?: string;
  border?: React.CSSProperties['border'];
  padding?: React.CSSProperties['padding'];
  imageWidth?: string;
  imageMinHeight?: string;
  halfImageHeight?: string;
  flex?: number;
  imageHeight?: string;
  initImageHeight?: string;
  isPortfolioPage?: boolean;
  allocation: string;
};
const PortfolioCardComponent: React.FC<PortfolioCardProps> = ({
  title,
  titleColor = COLORS.BLACK_TITLE,
  category,
  technologies,
  expertises,
  imageUrl,
  allocation,
  caption = '',
  width = '440px',
  flexDirection = 'column',
  arrowBorder = '1px solid black',
  arrowBackground = 'transparent',
  textBackground = 'transparent',
  descriptionColor = COLORS.CARD_TAGS_COLOR,
  titleSize = 24,
  titleWeight = 600,
  titleLineHeight = 36,
  tagBorder = '1px solid #0000001a',
  alignContent,
  border = '1px solid black',
  padding = '24px',
  imageWidth = 'none',
  imageHeight = '340px',
  halfImageHeight = '170px',
  flex,
  imageMinHeight = '0px',
  initImageHeight,
  isPortfolioPage = true,
}) => {
  const { t } = useTranslation();
  const resolvedImageUrl = imageUrl || PortfolioHome;
  const resolvedAllocation =
    allocation === 'Take part in development'
      ? t('utils.card.allocation.take_part')
      : t('utils.card.allocation.scratch');
  const facetsListRef = useRef<HTMLDivElement>(null);

  const allTags = useMemo(
    () => [...(category || []), ...(technologies || []), ...(expertises || [])],
    [category, technologies, expertises]
  );

  const [visibleTags, setVisibleTags] = useState<string[]>(allTags);

  const SetTags = useCallback(() => {
    const MAX_HEIGHT = window.innerWidth > 900 ? 140 : 110;
    const container = facetsListRef.current;
    if (!container) return;
    const children = Array.from(container.children) as HTMLElement[];
    if (children.length !== allTags.length) return;
    const containerTop = container.offsetTop;
    let lastVisibleIndex = children.length;
    for (let i = 0; i < children.length; i++) {
      const item = children[i];
      const bottom = item.offsetTop - containerTop;
      if (bottom > MAX_HEIGHT) {
        lastVisibleIndex = i;
        break;
      }
    }

    if (lastVisibleIndex < children.length) {
      setVisibleTags([...allTags.slice(0, lastVisibleIndex - 1), '__more__']);
    } else {
      setVisibleTags(allTags);
    }
  }, [allTags]);

  useEffect(() => {
    SetTags();
    window.addEventListener('resize', SetTags);
    return () => {
      window.removeEventListener('resize', SetTags);
    };
  }, [allTags, SetTags]);

  return (
    <Card
      $width={width}
      $isPortfolioPage={isPortfolioPage}
      $flexDirection={flexDirection}
      $border={border}
      $imageHeight={halfImageHeight}
      title={title}
    >
      {resolvedImageUrl ? (
        <ImageWrapper
          to={`${ROUTES.PROJECT}/${title?.replace(/\s+/g, '_')}`}
          $isPortfolioPage={isPortfolioPage}
          $imageWidth={imageWidth}
          $imageHeight={imageHeight}
          $imageMinHeight={imageMinHeight}
          $halfImageHeight={halfImageHeight}
        >
          <CardImage
            src={resolvedImageUrl}
            alt={title}
            draggable={false}
            $flex={flex}
            $isPortfolioPage={isPortfolioPage}
            $imageWidth={imageWidth}
            $imageHeight={imageHeight}
            $imageMinHeight={imageMinHeight}
            $initImageHeight={initImageHeight}
          />
        </ImageWrapper>
      ) : null}
      <CardContent
        $textBackground={textBackground}
        $alignContent={alignContent}
        $padding={padding}
        $isPortfolioPage={isPortfolioPage}
      >
        <Link to={`${ROUTES.PROJECT}/${title?.replace(/\s+/g, '_')}`}>
          {resolvedAllocation && (
            <AllocationContainer>
              <Text
                text={resolvedAllocation}
                size={16}
                weight={400}
                lineHeight={24}
              />
            </AllocationContainer>
          )}
          <Title
            title={title}
            size={titleSize}
            weight={titleWeight}
            lineHeight={titleLineHeight}
            color={titleColor}
          />
          <CaptionWrapper>
            {caption ? (
              <Text
                text={caption}
                size={22}
                weight={400}
                lineHeight={33}
                color={COLORS.SECTION_TEXT}
              />
            ) : null}
          </CaptionWrapper>
        </Link>
        <DescriptionWrapper>
          <FacetsList ref={facetsListRef}>
            {visibleTags.map(tag =>
              tag === '__more__' ? (
                <Tag $tagBorder={tagBorder} key="more">
                  <Text
                    text="and more"
                    color={descriptionColor}
                    size={14}
                    lineHeight={21}
                  />
                </Tag>
              ) : (
                <Tag $tagBorder={tagBorder} key={tag}>
                  <Text
                    text={tag}
                    color={descriptionColor}
                    size={14}
                    lineHeight={21}
                  />
                </Tag>
              )
            )}
          </FacetsList>
          <RedArrowWrapper
            $arrowBorder={arrowBorder}
            $arrowBackground={arrowBackground}
          >
            <Link to={`${ROUTES.PROJECT}/${title?.replace(/\s+/g, '_')}`}>
              <Text
                text={'View case'}
                color={COLORS.WHITE}
                size={18}
                lineHeight={27}
              />
              {arrowBackground === COLORS.PRODUCTS_ARROW ? (
                <WhiteArrow />
              ) : (
                <RedArrow />
              )}
            </Link>
          </RedArrowWrapper>
        </DescriptionWrapper>
      </CardContent>
    </Card>
  );
};

const Card = styled(MotionPortfolioProjectCard)<{
  $width: string;
  $flexDirection: string;
  $border: React.CSSProperties['border'];
  $isPortfolioPage: boolean;
  $imageHeight: string;
}>`
  position: relative;
  width: ${props => props.$width};
  display: flex;
  flex-direction: ${props => props.$flexDirection};
  align-items: stretch;
  border: ${props => props.$border};
  box-sizing: content-box;
  background: ${COLORS.WHITE};
  padding-top: ${props => props.$imageHeight};
  margin-top: ${props => props.$imageHeight};

  &:hover {
    box-shadow: ${SHADOW.HOVER.SERVICE_CARD};
  }
  @media screen and (${device.md}) {
    max-width: 250px;
    padding-top: 100px;
    margin-top: 100px;
  }
  @media screen and (${device.sm}) {
    max-width: 300px;
    flex-shrink: 0;
    padding-top: 70px;
    margin-top: 70px;
  }
  @media screen and (${device.xs}) {
    max-width: none;
  }
`;
const ImageWrapper = styled(Link)<{
  $imageWidth: string;
  $imageHeight: string;
  $imageMinHeight?: string;
  $isPortfolioPage: boolean;
  $halfImageHeight: string;
}>`
  position: absolute;
  max-width: ${props => props.$imageWidth};
  min-height: ${props => props.$imageMinHeight};
  max-height: ${props => props.$imageHeight};
  display: flex;
  justify-content: center;
  cursor: default;
  align-items: center;
  top: ${props => `-${props.$halfImageHeight}`};
  right: 0;
  left: 0;
  @media screen and (${device.md}) {
    top: -100px;
  }
  @media screen and (${device.sm}) {
    top: -70px;
  }
`;
const CardImage = styled.img<{
  $imageWidth: string;
  $imageHeight: string;
  $flex?: number;
  $imageMinHeight?: string;
  $initImageHeight?: string;
  $isPortfolioPage: boolean;
}>`
  max-width: ${props => props.$imageWidth};
  object-fit: contain;
  z-index: 2;
  min-height: ${props => props.$imageMinHeight};
  height: ${props => props.$imageHeight};
  box-sizing: border-box;
  padding: 0 40px;
  @media screen and (${device.md}) {
    min-height: 130px;
    height: 200px;
  }
  @media screen and (${device.sm}) {
    min-height: 100px;
    height: 140px;
  }
`;

const CardContent = styled.div<{
  $textBackground: string;
  $alignContent?: string;
  $padding?: React.CSSProperties['padding'];
  $isPortfolioPage?: boolean;
}>`
  padding: ${props => props.$padding};
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 20px;
  box-sizing: border-box;
  ${props => props.$alignContent && `align-content: ${props.$alignContent}`};
  justify-content: space-between;

  a {
    text-decoration: none;
  }
  > a {
    display: flex;
    flex-direction: column;
    cursor: default;
  }
  > a > span {
    display: block;
    min-height: 72px;
    line-height: 36px;
  }

  @media screen and (${device.md}) {
    padding: 20px;
    gap: 15px;
    > a > span {
      font-size: 16px;
      line-height: 20px;
      min-height: 50px;
    }
  }
  @media screen and (${device.sm}) {
    padding: 10px;
    gap: 10px;
    > a > span {
      min-height: 20px;
    }
  }
`;

const AllocationContainer = styled.div`
  padding: 5px 10px;
  margin-bottom: 20px;
  border: 1px solid ${COLORS.MAIN_RED};
  align-self: flex-start;
  h4 {
    color: ${COLORS.MAIN_RED};
    display: block;
  }
  @media screen and (${device.sm}) {
    padding: 5px;
    h4 {
      font-size: 12px;
      line-height: 1.5;
      text-align: center;
    }
  }
`;

const CaptionWrapper = styled.div`
  > h4 {
    min-height: 99px;
  }
  @media screen and (${device.md}) {
    > h4 {
      font-size: 14px;
      line-height: 1.5;
      min-height: 70px;
    }
  }
  @media screen and (${device.sm}) {
    > h4 {
      min-height: auto;
    }
  }
`;
const DescriptionWrapper = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 48px;
  justify-content: space-between;

  @media screen and (${device.md}) {
    gap: 15px;
    > span {
      font-size: 16px;
      line-height: 20px;
      min-height: 0;
    }
  }
  @media screen and (${device.sm}) {
    gap: 10px;
  }
`;

const FacetsList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  @media screen and (${device.md}) {
    gap: 10px;
  }
`;

const RedArrowWrapper = styled.div<{
  $arrowBorder: React.CSSProperties['border'];
  $arrowBackground: string;
}>`
  border: ${props => props.$arrowBorder};
  background-color: ${props => props.$arrowBackground};

  > a {
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 10px;
  }
  &:hover {
    background: ${COLORS.RED_HOVER};
  }
  &:active {
    background: ${COLORS.RED_PRESSED};
  }
  width: 170px;
  height: 55px;

  h4 {
    text-decoration: none;
  }

  @media screen and (${device.md}) {
    width: 130px;
    height: 35px;
    h4 {
      font-size: 14px;
      line-height: 20px;
    }

    svg {
      width: 13px;
      height: 13px;
    }
  }
  @media screen and (${device.sm}) {
    width: 100px;
    height: 25px;
    h4 {
      font-size: 10px;
      line-height: 15px;
    }

    svg {
      width: 8px;
      height: 8px;
    }
  }
`;

const areEqual = (
  prevProps: PortfolioCardProps,
  nextProps: PortfolioCardProps
) => {
  return (
    prevProps.title === nextProps.title &&
    JSON.stringify(prevProps.category) === JSON.stringify(nextProps.category) &&
    JSON.stringify(prevProps.technologies) ===
      JSON.stringify(nextProps.technologies) &&
    JSON.stringify(prevProps.expertises) ===
      JSON.stringify(nextProps.expertises) &&
    prevProps.allocation === nextProps.allocation &&
    prevProps.caption === nextProps.caption &&
    prevProps.width === nextProps.width &&
    prevProps.flexDirection === nextProps.flexDirection &&
    prevProps.arrowBorder === nextProps.arrowBorder &&
    prevProps.arrowBackground === nextProps.arrowBackground &&
    prevProps.textBackground === nextProps.textBackground &&
    prevProps.descriptionColor === nextProps.descriptionColor &&
    prevProps.titleSize === nextProps.titleSize &&
    prevProps.titleWeight === nextProps.titleWeight &&
    prevProps.titleLineHeight === nextProps.titleLineHeight &&
    prevProps.tagBorder === nextProps.tagBorder &&
    prevProps.alignContent === nextProps.alignContent &&
    prevProps.border === nextProps.border &&
    prevProps.padding === nextProps.padding &&
    prevProps.imageWidth === nextProps.imageWidth &&
    prevProps.imageHeight === nextProps.imageHeight &&
    prevProps.halfImageHeight === nextProps.halfImageHeight &&
    prevProps.initImageHeight === nextProps.initImageHeight &&
    prevProps.isPortfolioPage === nextProps.isPortfolioPage
  );
};

export const PortfolioCard = memo(PortfolioCardComponent, areEqual);
